/* Data fetching with a stale-while-revalidate cache.
 *
 * The cache is what makes navigation feel instant: going back to the fleet
 * shows the previous answer immediately and quietly refreshes it, instead of
 * flashing a spinner over data that is two seconds old. Everything here is a
 * read of a small JSON document, so the memory cost is nil and the correctness
 * risk is bounded -- writes invalidate explicitly.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, api } from './api'

const cache = new Map<string, unknown>()

export function invalidate(prefix?: string) {
  if (!prefix) return cache.clear()
  for (const k of [...cache.keys()]) if (k.startsWith(prefix)) cache.delete(k)
}

export interface Query<T> {
  data: T | undefined
  error: ApiError | null
  loading: boolean
  /** True only on the very first load, when there is nothing to show yet.
   *  Distinguishing it from `loading` is what allows a background refresh to
   *  be silent rather than replacing the page with skeletons. */
  cold: boolean
  reload: () => void
  /** Reload and say whether it worked.
   *
   *  `reload` fires and forgets, which is right when a refresh failing is
   *  merely a page that stays stale. It is wrong when the refresh is the second
   *  half of something a person just did: after saving a lice count the write
   *  has already succeeded, and a failed re-fetch must say "the forecast could
   *  not be rebuilt", never "your count was lost". */
  reloadAsync: () => Promise<boolean>
  /** Replace what is on screen without a round trip, cache included.
   *
   *  For the case where a write already returned the new state — the lice POST
   *  answers with the stored counts — so the table updates on the same paint as
   *  the form closing, while the slower forecast catches up behind it. */
  patch: (fn: (prev: T | undefined) => T | undefined) => void
}

export function useApi<T>(path: string | null, opts?: { poll?: number }): Query<T> {
  const [data, setData] = useState<T | undefined>(
    () => (path ? (cache.get(path) as T | undefined) : undefined),
  )
  const [error, setError] = useState<ApiError | null>(null)
  const [loading, setLoading] = useState(!!path)
  const seq = useRef(0)

  const run = useCallback(async (): Promise<boolean> => {
    if (!path) return false
    const mine = ++seq.current
    setLoading(true)
    try {
      const d = await api.get<T>(path)
      // A slow earlier request must not overwrite a fast later one. Without
      // this, typing in a filter box can leave the results of an abandoned
      // query on screen.
      if (mine !== seq.current) return false
      cache.set(path, d)
      setData(d)
      setError(null)
      return true
    } catch (e) {
      if (mine !== seq.current) return false
      setError(e instanceof ApiError ? e : new ApiError(0, 'network'))
      return false
    } finally {
      if (mine === seq.current) setLoading(false)
    }
  }, [path])

  const patch = useCallback((fn: (prev: T | undefined) => T | undefined) => {
    setData((prev) => {
      const next = fn(prev)
      if (path && next !== undefined) cache.set(path, next)
      return next
    })
  }, [path])

  useEffect(() => {
    if (!path) {
      setData(undefined)
      setLoading(false)
      return
    }
    setData(cache.get(path) as T | undefined)
    void run()
  }, [path, run])

  useEffect(() => {
    if (!opts?.poll || !path) return
    const id = setInterval(() => {
      // Polling a hidden tab is spend with nobody watching, and on a live page
      // it is a request per second per abandoned tab.
      if (document.visibilityState === 'visible') void run()
    }, opts.poll)
    return () => clearInterval(id)
  }, [opts?.poll, path, run])

  return {
    data,
    error,
    loading,
    cold: loading && data === undefined,
    reload: () => { void run() },
    reloadAsync: run,
    patch,
  }
}
